import { useAtom, useAtomValue } from "jotai";
import {
  FC,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  indyGlassesVariantAtom,
  indyLauncherPositionAtom,
} from "~/atoms/chatbot";
import { IndyWordmark } from "~/pages/indy/components/chatSurfaceShared";
import "./ChatbotLauncher.css";
import { glassesById } from "./indyGlasses";
import {
  clampLauncherPosition,
  type LauncherPosition,
  resolveLauncherPosition,
  tooltipOnRight,
} from "./indyLauncherPosition";

/**
 * Bottom-right launcher for the Indy chat panel — a circular button carrying the
 * user's chosen hand-drawn "Indy glasses" (see indyGlasses.ts, picked via
 * IndyGlassesPicker). Lives on the dashboard and appointment pages, mounted
 * globally by MainLayout and hidden while the panel (or /indy) is open.
 *
 * Draggable: parked in the corner it covered the page-number controls on the
 * medical-record editor, so it can be dragged anywhere in the viewport and the
 * spot is held for as long as the tab stays on the page — in memory, reset by a
 * reload (`indyLauncherPositionAtom`). ChatbotModal reads the same position and
 * opens the panel off whichever corner the button now sits in, so moving the
 * button moves Indy as a whole rather than splitting it in two. Geometry —
 * defaults, clamping, the panel's opening corner — lives in
 * indyLauncherPosition.ts.
 *
 * Presentational otherwise: the parent owns `chatbotOpenAtom` and passes
 * `onOpen`. All motion is transform/opacity on the glyph/specs — see
 * ChatbotLauncher.css:
 *   - resting → a slow idle float
 *   - hover   → the frames "look up" (perk) + an "Ask Indy" tooltip to the left
 *   - drag    → both stilled, so the disc tracks the pointer and nothing else
 * The reading-scan gesture once parked here ships as IndyScanIndicator, where
 * the caller has a real streaming trigger.
 */
interface ChatbotLauncherProps {
  /** Open the Indy panel. Owned by the parent (keeps this presentational). */
  onOpen: () => void;
}

/**
 * How far the pointer must travel before a press counts as a drag rather than a
 * click. Without it every open would also nudge the button by the pixel or two
 * a hand moves during a click; with it, a real drag still suppresses the click
 * that the browser fires on release (see `draggedRef`).
 */
const DRAG_THRESHOLD_PX = 4;

export const ChatbotLauncher: FC<ChatbotLauncherProps> = ({ onOpen }) => {
  const g = glassesById(useAtomValue(indyGlassesVariantAtom));
  const [saved, setSaved] = useAtom(indyLauncherPositionAtom);

  // Live position while a drag is in flight. Kept local so the atom takes one
  // write per drag instead of one per pointermove — every subscriber, including
  // the panel's opening-corner maths, would otherwise re-render on each frame.
  const [dragPos, setDragPos] = useState<LauncherPosition | null>(null);
  const [dragging, setDragging] = useState(false);
  // Raised once a press crosses the threshold and lowered a tick after the
  // release, which is exactly long enough to swallow the click that ends the
  // drag. See `finish` for why it is not the click handler that lowers it.
  const draggedRef = useRef(false);

  const moved = dragPos !== null || saved !== null;
  const pos = dragPos ?? resolveLauncherPosition(saved);

  // A window narrow enough to put the parked spot out of bounds is corrected in
  // place, not just on render: `pos` is already clamped for painting, but the
  // held value has to follow or the button would jump back out the moment the
  // window is widened again.
  useEffect(() => {
    if (!saved) return;
    const onResize = () => {
      setSaved((prev) => {
        if (!prev) return prev;
        const next = clampLauncherPosition(prev);
        return next.x === prev.x && next.y === prev.y ? prev : next;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [saved, setSaved]);

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      // Left button / primary touch only — a right-click must stay a
      // right-click, and a middle-click must not drag the disc away.
      if (e.button !== 0) return;

      const el = e.currentTarget;
      const startX = e.clientX;
      const startY = e.clientY;
      // Origin comes from the resolved position, NOT getBoundingClientRect():
      // the hover rule scales and lifts the disc, so the measured box is 3px
      // above the layout box and the button would jump on grab.
      const origin = resolveLauncherPosition(saved);
      let latest = origin;
      draggedRef.current = false;

      // Pointer capture keeps move/up coming to the button even when the
      // pointer outruns it, which it will — the disc only catches up on the
      // next frame's paint.
      el.setPointerCapture(e.pointerId);

      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        if (!draggedRef.current) {
          if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
          draggedRef.current = true;
          setDragging(true);
        }
        latest = clampLauncherPosition({ x: origin.x + dx, y: origin.y + dy });
        setDragPos(latest);
      };

      const finish = () => {
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerup", finish);
        el.removeEventListener("pointercancel", finish);
        el.removeEventListener("lostpointercapture", finish);
        setDragging(false);
        if (draggedRef.current) {
          // Commit and drop the live position in one batch: `pos` falls
          // straight through to the value just saved, so there is no frame
          // where the button snaps back to where the drag began.
          setSaved(latest);
          // Drop the drag guard on a timer rather than in the click handler.
          // A drag clamped against a viewport edge leaves the pointer off the
          // disc, so the click that follows the release lands somewhere else
          // entirely and never reaches us — a guard waiting to be cleared
          // there would go on to eat the next real click on the button.
          setTimeout(() => {
            draggedRef.current = false;
          }, 0);
        }
        setDragPos(null);
      };

      el.addEventListener("pointermove", onMove);
      el.addEventListener("pointerup", finish);
      el.addEventListener("pointercancel", finish);
      // Safety net: a capture lost to a context menu or a device unplugged
      // mid-drag never delivers pointerup.
      el.addEventListener("lostpointercapture", finish);
    },
    [saved, setSaved],
  );

  const handleClick = useCallback(() => {
    // The release that ends a drag also fires a click. Swallow that one.
    if (draggedRef.current) return;
    onOpen();
  }, [onOpen]);

  const className = [
    "indy-launcher",
    dragging ? "indy-launcher--dragging" : "",
    tooltipOnRight(pos) ? "indy-launcher--tip-right" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={className}
      aria-label="Ask Indy (drag to move)"
      // Only once it has actually been moved. Left alone, the button keeps
      // the stylesheet's right/bottom insets, which re-anchor to the corner on
      // resize for free; inline left/top would freeze it mid-viewport instead.
      // Blanking right/bottom lets left/top win outright, ≤640px rule included.
      style={
        moved
          ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
          : undefined
      }
      onPointerDown={handlePointerDown}
      onClick={handleClick}
    >
      <span className="indy-launcher__tip">
        Ask <IndyWordmark />
      </span>
      <svg
        className="indy-launcher__glyph"
        viewBox={`0 0 ${g.vbW} 40`}
        fill="none"
        aria-hidden="true"
      >
        <g className="indy-launcher__specs">
          <image
            href={g.src}
            x="0"
            y="0"
            width={g.vbW}
            height="40"
            preserveAspectRatio="xMidYMid meet"
          />
        </g>
      </svg>
    </button>
  );
};

export default ChatbotLauncher;
